import React, { useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import Button from './Button';
import Modal from './Modal';

import styles from '../../styles/views/components/DefaultModal.module.scss';

const MAX_HEIGHT = 596;

const DefaultModal = ({
  isShown,
  primaryAction,
  isPrimaryBtnDisabled,
  closeModal,
  secondaryAction,
  message,
  title,
  secondaryBtnText,
  isSecondaryBtnDisabled,
  primaryBtnText,
  primaryBtnTooltip,
  renderButtons,
  children,
  closeIconExtraClass,
}) => {
  const modalRef = useRef(null);

  const shouldAddWhiteShadow = useMemo(
    () => modalRef.current?.offsetHeight >= MAX_HEIGHT,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalRef.current?.offsetHeight],
  );

  return (
    <Modal
      isShown={isShown}
      onClose={closeModal}
      modalExtraClass={styles.modalExtraStyles}
      closeIconExtraClass={closeIconExtraClass}
    >
      <div ref={modalRef} className={styles.wrapper}>
        <h2 className={styles.heading}>{title}</h2>
        <hr className={styles.hrLine} />
        <div
          className={`${styles.mainContent} ${children ? styles.adjustedMargin : ''}`}
          style={shouldAddWhiteShadow ? { marginBottom: '0' } : {}}
        >
          <div className={styles.message}>
            {message}
          </div>
          <div style={shouldAddWhiteShadow ? { marginBottom: '24px' } : {}}>
            {children}
          </div>
        </div>
        {shouldAddWhiteShadow && <div className={styles.whiteShadow} />}
        <div className={styles.actions}>
          {renderButtons ? renderButtons()
            : (
              <>
                {secondaryBtnText && (
                <Button
                  onClick={() => {
                    if (secondaryAction) {
                      return secondaryAction();
                    }
                    return closeModal();
                  }}
                  value={secondaryBtnText}
                  variant="secondary"
                  color="gold"
                  extraStyles={{ marginRight: '8px' }}
                  name="secondaryAction"
                  isDisabled={isSecondaryBtnDisabled}
                />
                )}
                {primaryBtnText && (
                  <Button
                    onClick={primaryAction}
                    value={primaryBtnText}
                    variant="primary"
                    color="gold"
                    name="primaryAction"
                    isDisabled={isPrimaryBtnDisabled}
                    tooltip={primaryBtnTooltip}
                  />
                )}
              </>
            )}
        </div>
      </div>
    </Modal>
  );
};

DefaultModal.propTypes = {
  isShown: PropTypes.bool,
  primaryAction: PropTypes.func,
  closeModal: PropTypes.func.isRequired,
  secondaryAction: PropTypes.func,
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  title: PropTypes.string.isRequired,
  secondaryBtnText: PropTypes.string,
  primaryBtnText: PropTypes.string,
  primaryBtnTooltip: PropTypes.string,
  renderButtons: PropTypes.func,
  children: PropTypes.node,
  isPrimaryBtnDisabled: PropTypes.bool,
  isSecondaryBtnDisabled: PropTypes.bool,
  closeIconExtraClass: PropTypes.string,
};

DefaultModal.defaultProps = {
  isShown: false,
  primaryAction: null,
  secondaryAction: null,
  secondaryBtnText: '',
  primaryBtnText: '',
  primaryBtnTooltip: '',
  renderButtons: null,
  children: null,
  isPrimaryBtnDisabled: false,
  isSecondaryBtnDisabled: false,
  message: '',
  closeIconExtraClass: '',
};

export default DefaultModal;
